import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { Scope, showApiErrorToast, showErrorToast } from '@utils'
import { useLanguage } from '@context'

type AnswerRequestedContacts = 'CONFIRM' | 'DECLINE'

export interface AnswerRequestedContactsParams {
  requestedContactId: string
  answer: AnswerRequestedContacts
  scope?: Scope
}

const answerRequestedContacts = async ({
  requestedContactId,
  answer,
  scope,
}: AnswerRequestedContactsParams): Promise<undefined> => {
  const endpoint = `/contact-requests/${requestedContactId}/answer`
  const { data: responseData } = await api.post<undefined>(endpoint, {
    response: answer,
    scope,
  })
  return responseData
}

export const useAnswerRequestedContacts = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<undefined, Error, AnswerRequestedContactsParams>({
    mutationFn: answerRequestedContacts,
    // On the mutation itself, not on each mutate() call: those callbacks are
    // lost once the screen is closed, and a failed answer said nothing.
    onError: (error) => {
      const response = (error as { response?: { status?: number; data?: { code?: string } } })
        .response
      // Cancelled or sent again by the other person, or answered on another
      // device: the lists are read again below, so the card goes away.
      if (
        response?.status === 404 ||
        response?.data?.code === 'Keeptouch.ContactRequest.AnswerAlreadyProvided'
      ) {
        showErrorToast(t('errors.title'), t('notifications.requestGone'))
      } else {
        showApiErrorToast(t, error)
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['contact-requests'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['contacts'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['suggested-contacts'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['notifications'],
        }),
        // The other person's profile was cached while they were still a
        // stranger (name, photo, bio and hashtags only): ask for it again so
        // the full profile shows at once. My own profile does not change.
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'profile' && query.queryKey[1] !== 'me',
        }),
      ])
    },
  })
}
